/**
 * Resizes an image if it exceeds the maximum dimension.
 * Maintains aspect ratio and converts to JPEG with specified quality.
 * 
 * @param file - The original image file
 * @param maxDimension - Maximum width or height in pixels (default: 2000)
 * @param quality - JPEG quality from 0 to 1 (default: 0.85)
 * @returns Promise<File> - The resized file (or original if no resize needed)
 */
export const resizeImageIfNeeded = async (
  file: File,
  maxDimension: number = 2000,
  quality: number = 0.85
): Promise<File> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      const { width, height } = img;
      
      // Check if resize is needed
      if (width <= maxDimension && height <= maxDimension) {
        // No resize needed, return original file
        URL.revokeObjectURL(img.src);
        resolve(file);
        return;
      }
      
      // Calculate new dimensions maintaining aspect ratio
      let newWidth = width;
      let newHeight = height;
      
      if (width > height) {
        if (width > maxDimension) {
          newHeight = Math.round(height * (maxDimension / width));
          newWidth = maxDimension;
        }
      } else {
        if (height > maxDimension) {
          newWidth = Math.round(width * (maxDimension / height));
          newHeight = maxDimension;
        }
      }
      
      // Create canvas and resize
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        URL.revokeObjectURL(img.src);
        reject(new Error('Failed to get canvas context'));
        return;
      }
      
      canvas.width = newWidth;
      canvas.height = newHeight;
      ctx.drawImage(img, 0, 0, newWidth, newHeight);
      
      // Convert to blob
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(img.src);
          
          if (!blob) {
            reject(new Error('Failed to create resized image blob'));
            return;
          }
          
          // Create new file with same name but .jpg extension if needed
          const newFileName = file.name.replace(/\.[^.]+$/, '.jpg');
          const resizedFile = new File([blob], newFileName, { type: 'image/jpeg' });
          
          console.log(`Image resized: ${width}x${height} -> ${newWidth}x${newHeight}, ${(file.size / 1024 / 1024).toFixed(2)}MB -> ${(resizedFile.size / 1024 / 1024).toFixed(2)}MB`);
          
          resolve(resizedFile);
        },
        'image/jpeg',
        quality
      );
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image for resizing'));
    };
    
    img.src = URL.createObjectURL(file);
  });
};
